/**
 * Lake-agent runtime hooks — mirror of `useAgentRuntime.ts` but talking to
 * the per-user lake endpoints. Kept as a sibling module so the case-agent
 * hooks stay untouched (different query keys + endpoints).
 */

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import {
  createLakeThread,
  deleteLakeThread,
  getLakeAgentHistory,
  getLakeThreadHealth,
  listLakeThreads,
  renameLakeThread,
} from '@/services/api/lakeAgent'

export const lakeAgentHistoryKey = (threadId: string) =>
  ['lake-agent', 'history', threadId] as const

export const lakeAgentThreadsKey = () => ['lake-agent', 'threads'] as const

export const lakeThreadHealthKey = (threadId: string) =>
  ['lake-agent', 'thread-health', threadId] as const

/** Token usage + banner-trigger status for a lake thread. Refetched on mount
 *  and after each chat turn (invalidated by the host on stream completion).
 *  With `enabled: false` nothing is asked: the chat host calls it in case scope too. */
export function useLakeThreadHealth(threadId: string, { enabled }: { enabled: boolean }) {
  return useQuery({
    queryKey: lakeThreadHealthKey(threadId),
    queryFn: () => getLakeThreadHealth(threadId),
    enabled,
    staleTime: 15_000,
  })
}

const HISTORY_PAGE_SIZE = 30

/** A lake thread's messages, a page at a time. With `enabled: false` nothing is asked of the
 *  server: the chat host calls it in case scope too, where the history is the case's. */
export function useLakeAgentHistory(threadId: string, { enabled }: { enabled: boolean }) {
  const query = useInfiniteQuery({
    queryKey: lakeAgentHistoryKey(threadId),
    queryFn: ({ pageParam }) =>
      getLakeAgentHistory(threadId, {
        limit: HISTORY_PAGE_SIZE,
        before: pageParam ?? undefined,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: lastPage =>
      lastPage.has_more ? (lastPage.next_cursor ?? undefined) : undefined,
    staleTime: 60_000,
    enabled,
  })

  const messages = useMemo(() => {
    const pages = query.data?.pages ?? []
    return [...pages].reverse().flatMap(p => p.messages)
  }, [query.data])

  return {
    messages,
    isLoading: query.isLoading,
    hasMore: query.hasNextPage,
    isFetchingMore: query.isFetchingNextPage,
    fetchMore: query.fetchNextPage,
  }
}

export function useLakeThreadList() {
  const queryClient = useQueryClient()

  const invalidateAll = () => queryClient.invalidateQueries({ queryKey: lakeAgentThreadsKey() })

  const query = useQuery({
    queryKey: lakeAgentThreadsKey(),
    queryFn: () => listLakeThreads(),
    staleTime: 30_000,
  })

  const create = useMutation({
    mutationFn: () => createLakeThread(),
    onSuccess: invalidateAll,
  })

  const remove = useMutation({
    mutationFn: (threadId: string) => deleteLakeThread(threadId),
    onSuccess: invalidateAll,
  })

  const rename = useMutation({
    mutationFn: ({ threadId, title }: { threadId: string; title: string }) =>
      renameLakeThread(threadId, title),
    onSuccess: invalidateAll,
  })

  return {
    threads: query.data ?? [],
    isLoading: query.isLoading,
    create,
    remove,
    rename,
  }
}
